import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type Step = 1 | 2 | 3 | 4;

interface StepNavigationProps {
  currentStep: Step;
  onStepChange: (step: Step) => void;
  accessibleSteps?: Step[]; // Steps that can be clicked (defaults to steps up to currentStep)
}

const steps = [
  { number: 1, title: 'Research', description: 'Find & shortlist venues' },
  { number: 2, title: 'RFPs', description: 'Track with Cvent' },
  { number: 3, title: 'Selection', description: 'Review & finalize venues' },
  { number: 4, title: 'Proposal', description: 'Generate & send proposal' },
];

export function StepNavigation({ currentStep, onStepChange, accessibleSteps }: StepNavigationProps) {
  const currentStepData = steps.find(s => s.number === currentStep);
  
  // Determine which steps are clickable
  // If accessibleSteps is provided, use it; otherwise, default to steps up to currentStep
  const getIsClickable = (stepNumber: Step): boolean => {
    if (accessibleSteps) {
      return accessibleSteps.includes(stepNumber);
    }
    return stepNumber <= currentStep;
  };
  
  return (
    <nav aria-label="Progress">
      {/* Mobile: Show current step info */}
      <div className="sm:hidden mb-3">
        <p className="text-sm font-medium text-gray-900">{currentStepData?.title}</p>
        <p className="text-xs text-muted-foreground">{currentStepData?.description}</p>
      </div>
      
      <ol className="flex items-center justify-between max-w-4xl">
        {steps.map((step, index) => {
          const isCompleted = step.number < currentStep;
          const isCurrent = step.number === currentStep;
          const isClickable = getIsClickable(step.number as Step);

          return (
            <li key={step.number} className="flex items-center flex-1">
              <button
                onClick={() => isClickable && onStepChange(step.number as Step)}
                disabled={!isClickable}
                className={cn(
                  'flex items-center gap-3 text-left transition-colors',
                  isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-50'
                )}
              >
                {/* Step Circle */}
                <div
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all',
                    {
                      'bg-primary-600 border-primary-600 text-white': isCurrent || isCompleted,
                      'bg-white border-gray-300 text-gray-500': !isCurrent && !isCompleted,
                    }
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{step.number}</span>
                  )}
                </div>

                {/* Step Info - Desktop only */}
                <div className="hidden sm:block">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      isCurrent || isCompleted ? 'text-gray-900' : 'text-gray-500'
                    )}
                  >
                    {step.title}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </button>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2 transition-colors',
                    isCompleted ? 'bg-primary-600' : 'bg-gray-300'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

